/* eslint-disable @typescript-eslint/no-explicit-any */
export function updateFilter<T extends { name: string }>(
  currentFilter: Ref<T[]>,
  newFilter: T
) {
  // Replace old filter with new one if it exists
  const index = currentFilter.value.findIndex(
    (filter) => filter.name === newFilter.name
  )

  if (index !== -1) {
    currentFilter.value.splice(index, 1, newFilter)
  } else {
    currentFilter.value.push(newFilter)
  }
}

export function removeFilter<T extends { name: string }>(
  currentFilter: Ref<T[]>,
  filterName: string
) {
  currentFilter.value = currentFilter.value.filter(
    (filter) => filter.name !== filterName
  )
}

function mergeFilters<T extends { getFilter: () => any }>(
  filters: T[],
  mode: 'and' | 'or' = 'and'
) {
  const map: Record<string, any>[] = []

  switch (filters.length) {
    case 0:
      return {}
    case 1:
      return filters[0].getFilter()
    default:
      for (const filter of filters) {
        const filterObj = filter.getFilter() as {
          $or?: Record<string, any>[]
        }

        if (filterObj.$or && Array.isArray(filterObj.$or)) {
          const entryWithOr = map.find((entry) => Array.isArray(entry.$or))

          if (entryWithOr) {
            filterObj.$or.forEach((orCondition) => {
              entryWithOr.$or.push(orCondition)
            })
          } else {
            map.push({ $or: [...filterObj.$or] })
          }
        } else if (filterObj.$or) {
          // Unexpected type for $or
        } else {
          map.push(filterObj)
        }
      }

      return {
        [`$${mode}`]: map,
      }
  }
}

function urlEncodeFilters<T>(filters: T): Record<string, string> {
  const obj: Record<string, any> = { ...(filters as Record<string, any>) }

  Object.keys(obj).forEach((key) => {
    // on arrays with strings join them with commas
    if (
      Array.isArray(obj[key]) &&
      obj[key].every((item) => typeof item === 'string')
    ) {
      obj[key] = obj[key].join(',')
    }

    // Delete false boolean values
    if (typeof obj[key] === 'boolean') {
      if (!obj[key]) {
        delete obj[key]
      }
    }

    // Remove Undefined and Null values
    if (obj[key] === undefined || obj[key] === null) {
      delete obj[key]
    }

    // Remove Empty Arrays
    if (Array.isArray(obj[key]) && obj[key].length === 0) {
      delete obj[key]
    }

    // Remove empty strings from the object
    if (typeof obj[key] === 'string' && obj[key].trim() === '') {
      delete obj[key]
    }
  })

  return obj
}

export default {
  updateFilter,
  removeFilter,
  mergeFilters,
  urlEncodeFilters,
}
